'use client';

// react
import { useCallback } from 'react';
// @mui
import {
  Grid,
  Input,
  FormControl,
  Container,
  IconButton,
  InputAdornment,
  Typography,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
// next
import { usePathname } from 'next/navigation';
// hooks
import { useShallow } from '@/hooks/use-shallow';
//
import { SEARCH_BY_TYPE } from './search-by-type.enum';

type SearchBarProps = {
  setSearch: (search: string) => void;
  search: string;
  header: string;
  placeholder: string;
  type?: string;
  searchByType?: SEARCH_BY_TYPE;
};

export default function SearchBar({
  setSearch,
  search,
  header,
  placeholder,
  type,
  searchByType = SEARCH_BY_TYPE.USER_GROUP,
}: SearchBarProps) {
  const { push, query } = useShallow();

  const pathname = usePathname();

  const { searchBy } = query;

  const handleSearch = useCallback(() => {
    if (type)
      push(`/${type}/search`, { query: { ...query, searchText: search }, changeLocation: true });
    else push(pathname ?? '', { query: { ...query, searchText: search }, changeLocation: true });
  }, [pathname, push, query, search, type]);

  const handleSearchByChange = useCallback(
    (e: any) => {
      push(pathname ?? '', { changeLocation: true, query: { ...query, searchBy: e.target.value } });
    },
    [pathname, push, query]
  );

  const keyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key == 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <Container maxWidth="lg">
      <Grid container spacing={2} alignItems="center" direction="row" sx={{ py: 2 }}>
        <Grid item xs={12} sm={3}>
          <Typography variant="h3">{header}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl sx={{ width: '100%' }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={keyPress}
              placeholder={placeholder}
              endAdornment={
                <InputAdornment position="end" sx={{ pr: 1, my: 1 }}>
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3} container alignItems="center" spacing={1} direction="row">
          <Grid item>
            <InputLabel>Search By:</InputLabel>
          </Grid>
          <Grid item>
            <Select variant="standard" value={searchBy ?? 1} onChange={handleSearchByChange}>
              <MenuItem value={1}>
                {searchByType === SEARCH_BY_TYPE.USER_GROUP ? 'Page Details' : 'Content Details'}
              </MenuItem>
              <MenuItem value={2}>
                {searchByType === SEARCH_BY_TYPE.USER_GROUP ? 'Member Names' : 'Organizer'}
              </MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
